import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage, useLp } from '../context/LanguageContext';
import { parseLocalized, localize } from '../lib/routes';
import { motion, AnimatePresence } from 'framer-motion';

const languages = [
  { code: 'it', label: 'IT', flag: '🇮🇹' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'de', label: 'DE', flag: '🇩🇪' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' }
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const { language, setLanguage: setLang, t } = useLanguage();
  const lp = useLp();
  const location = useLocation();
  const navigate = useNavigate();

  const setLanguage = (code) => {
    setLang(code);
    const parsed = parseLocalized(location.pathname);
    if (parsed) navigate(localize(parsed.itPath, code) + location.hash);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: lp('/'), label: t('nav.home') },
    { to: lp('/servizi'), label: t('nav.services') },
    { to: lp('/chi-siamo'), label: t('nav.about') },
    { to: lp('/contatti'), label: t('nav.contact') },
    { to: lp('/faq'), label: t('nav.faq') }
  ];

  const currentLang = languages.find(l => l.code === language);
  const lightPage = /^\/(login|area-clienti|imposta-password|richiesta-accesso|appuntamento)/.test(location.pathname);
  const solid = isScrolled || lightPage;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        solid 
          ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(40,56,128,0.06)] border-b border-[rgba(30,36,51,0.06)]' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to={lp('/')} 
            data-testid="navbar-logo"
            className="relative flex items-center h-10 md:h-12"
          >
            <img 
              src="/logo-light.png" 
              alt="Accordini Carillo Notai Associati" 
              className={`h-10 md:h-12 w-auto drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)] transition-opacity duration-500 ${solid ? 'opacity-0' : 'opacity-100'}`}
            />
            <img 
              src="/logo-dark.png" 
              alt="" 
              aria-hidden="true"
              className={`absolute left-0 top-0 h-10 md:h-12 w-auto transition-opacity duration-500 ${solid ? 'opacity-100' : 'opacity-0'}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`text-xs font-medium uppercase tracking-wider transition-colors ${
                  location.pathname === link.to
                    ? 'text-[#C9A227]'
                    : solid 
                      ? 'text-[#64748B] hover:text-[#283880]' 
                      : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Area Clienti Button */}
            <Link
              to="/login"
              data-testid="nav-area-clienti"
              className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
                solid 
                  ? 'bg-[#283880] text-white hover:bg-[#1B275B]' 
                  : 'bg-[#C9A227] text-[#121826] hover:bg-[#B38E1B] shadow-[0_2px_12px_rgba(201,162,39,0.3)]'
              }`}
            >
              {t('ui.nav.clientArea')}
            </Link>

            {/* Language Selector */}
            <div className="relative">
              <button
                data-testid="language-selector"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className={`flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors ${
                  solid 
                    ? 'text-[#64748B] hover:text-[#283880]' 
                    : 'text-white/90 hover:text-white'
                }`}
              >
                <span>{currentLang?.label}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 bg-white border border-slate-100 shadow-lg py-2 min-w-[100px]"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        data-testid={`lang-option-${lang.code}`}
                        onClick={() => {
                          setLanguage(lang.code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-xs uppercase tracking-wider transition-colors ${
                          language === lang.code
                            ? 'text-[#C9A227] bg-slate-50'
                            : 'text-[#64748B] hover:text-[#283880] hover:bg-slate-50'
                        }`}
                      >
                        {lang.flag} {lang.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 transition-colors ${
              solid ? 'text-[#283880]' : 'text-white'
            }`}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-link-${link.to.replace('/', '') || 'home'}`}
                  className={`block text-sm font-medium uppercase tracking-wider py-2 ${
                    location.pathname === link.to
                      ? 'text-[#C9A227]'
                      : 'text-[#64748B]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Language Selector */}
              <div className="pt-4 border-t border-slate-100">
                <div className="flex gap-4">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      data-testid={`mobile-lang-${lang.code}`}
                      onClick={() => setLanguage(lang.code)}
                      className={`text-sm font-medium ${
                        language === lang.code
                          ? 'text-[#C9A227]'
                          : 'text-[#64748B]'
                      }`}
                    >
                      {lang.flag} {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
